import type {
  KambiCouponPlaceSuccessResponse,
  KambiCouponUnauthorizedErrorResponse,
  KambiCouponValidateSuccessResponse,
} from '@mobi/api-types/kambi/player/coupon'
import { updateUserData } from '@core/Areas/KambiClient/Store'
import {
  selectActiveKambiJurisdiction,
  selectPlayerSessionId,
} from '@core/Areas/KambiClient/Store/selectors'
import { getWapi } from '@core/Areas/KambiClient/helpers'
import { post } from '@core/Services/ApiService'
import { store } from '@core/Store'
import { startListening } from '@core/Store/listenerMiddleware'
import type { KambiBettingParams, KambiPlaceBetBettingParams } from './types'

export const postValidateKambiSportsBet = ({ couponRows, bets }: KambiBettingParams) => {
  const offering = selectActiveKambiJurisdiction(store.getState())
  return postKambiBetToMobiApi<KambiBettingParams, KambiCouponValidateSuccessResponse>(
    '/api/kambibetting/validate-bet',
    { couponRows, bets, offering }
  )
}

export const postPlaceKambiSportsBet = ({
  couponRows,
  bets,
  rewards,
  copiedBetLoopPostId,
}: KambiBettingParams) => {
  const offering = selectActiveKambiJurisdiction(store.getState())
  return postKambiBetToMobiApi<KambiPlaceBetBettingParams, KambiCouponPlaceSuccessResponse>(
    '/api/kambibetting/place-bet',
    {
      requestId: createRequestId(),
      offering,
      couponRows,
      bets,
      rewards,
      copiedBetLoopPostId,
    }
  )
}

// =============
// Local Helpers
// =============

async function postKambiBetToMobiApi<TRequest, TResponse>(
  url: string,
  body: TRequest
): Promise<TResponse> {
  let kambiBearerToken = selectPlayerSessionId(store.getState())

  if (!kambiBearerToken) {
    kambiBearerToken = await getKambiPlayerToken()
  }

  if (!kambiBearerToken) {
    const unauthorizedKambiTokenError: KambiCouponUnauthorizedErrorResponse = {
      status: 401,
      message: 'Unauthorized',
    }
    throw unauthorizedKambiTokenError
  }

  const additionalHeaders = { ['X-Kambi-Bearer']: kambiBearerToken }
  return post<TResponse>({ url, body, additionalHeaders })
}

let sessionCommitCount = 0
function createRequestId(): string {
  return `${Date.now()}-${++sessionCommitCount}`
}

const getKambiPlayerToken = () =>
  new Promise<string>(resolve => {
    getWapi().then(wapi => {
      const dispose = startListening({
        actionCreator: updateUserData,
        effect: action => {
          if (action.payload) {
            resolve(action.payload)
            dispose()
          }
        },
      })
      wapi.request(wapi.USER_DATA)
    })
  })

// cspell:words Wapi kambibetting
